/**
 * Sentry client-side initialisation.
 *
 * Next.js / @sentry/nextjs picks this file up automatically.
 * Init is a no-op when NEXT_PUBLIC_SENTRY_DSN is not set so local dev and
 * CI environments without secrets stay clean.
 *
 * Operator checklist (production):
 *   1. Set NEXT_PUBLIC_SENTRY_DSN in your deployment environment.
 *   2. Set SENTRY_AUTH_TOKEN for source-map uploads at build time.
 *   3. Optionally tune `tracesSampleRate`.
 *
 * Replay lazy-load strategy (Sprint 19 hotfix #13):
 *   The Replay integration JS chunk (~21 KiB) is NOT loaded on page init.
 *   It is deferred until EITHER the user first interacts (pointerdown /
 *   keydown / scroll) OR ~5 s after the page becomes interactive via
 *   requestIdleCallback (with a setTimeout fallback). This eliminates the
 *   chunk from the initial payload, improving LCP and INP, while still
 *   capturing replays for error sessions (replaysOnErrorSampleRate: 0.1).
 */
;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"0EyL0Xstlr7TkjIeB8kty"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";import * as Sentry from "@sentry/nextjs";

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    // Capture 10 % of transactions for performance monitoring.
    // Raise toward 1.0 once you understand your event volume.
    tracesSampleRate: 0.1,
    // Replay disabled in the initial bundle — loaded lazily on error below.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    integrations: [],
  });

  if (typeof window !== "undefined") {
    // Wait for the page to be fully idle and the user to interact before loading
    // the replay integration. This prevents the ~21 KiB chunk from blocking LCP
    // and INP. We still capture replays for errors that happen after first input.
    const loadReplay = () => {
      Sentry.lazyLoadIntegration("replayIntegration")
        .then((replayIntegration) => {
          Sentry.addIntegration(replayIntegration());
        })
        .catch(() => {
          // Silently ignore — replay is non-critical telemetry.
        });
    };

    const triggerOnce = (() => {
      let fired = false;
      return () => {
        if (fired) return;
        fired = true;
        loadReplay();
      };
    })();

    // Either user interaction or 5s after page is interactive — whichever first.
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll"];
    events.forEach((ev) =>
      window.addEventListener(ev, triggerOnce, { once: true, passive: true })
    );

    // Fallback: idle callback ~5s after page becomes interactive.
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
    if (typeof idle === "function") {
      idle(triggerOnce, { timeout: 5000 });
    } else {
      setTimeout(triggerOnce, 5000);
    }
  }
}
